import { MetaTypesBase } from "@/autogen/common";
import type {
  GeneratedMeta,
  GeneratedProperty,
  FieldOverride,
} from "./contracts";

export function fieldLabel(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}
export function resolveFields(
  meta: GeneratedMeta,
  overrides: object = {},
  fields?: object,
) {
  const properties = new Map(
    meta.properties.map((property) => [property.propertyName, property]),
  );
  const options = new Map<string, FieldOverride>(Object.entries(overrides));
  for (const [key, override] of options)
    if (override.meta) properties.set(key, override.meta);
  return [...properties.values()]
    .filter(
      (property) =>
        !property.propertyName.endsWith("DidSet") &&
        !property.isPrimary &&
        !options.get(property.propertyName)?.hidden &&
        (!fields || property.propertyName in fields),
    )
    .map((meta) => ({
      meta: meta as GeneratedProperty,
      key: meta.propertyName,
      label:
        options.get(meta.propertyName)?.label ?? fieldLabel(meta.propertyName),
      readonly: options.get(meta.propertyName)?.readonly ?? false,
    }));
}
export function fieldKind(meta: GeneratedProperty) {
  if (meta.propertyType.isEnum) return "enum";
  if (meta.propertyType === MetaTypesBase.boolean) return "boolean";
  if (meta.propertyType === MetaTypesBase.LocalDate) return "date";
  if (meta.propertyType === MetaTypesBase.Date) return "datetime";
  if (
    meta.propertyType === MetaTypesBase.number ||
    meta.propertyType === MetaTypesBase.currency
  )
    return "number";
  if (meta.propertyType === MetaTypesBase.string) return "string";
  return "custom";
}
export function enumItems(meta: GeneratedProperty): (string | number)[] {
  const type = meta.propertyType;
  if (!("enum" in type) || typeof type.enum !== "object" || !type.enum)
    return [];
  return Object.entries(type.enum)
    .filter(([key]) => !/^\d+$/.test(key))
    .map(([, value]: [string, unknown]) => value)
    .filter(
      (value): value is string | number =>
        typeof value === "string" || typeof value === "number",
    );
}
export function readField(fields: object, key: string): unknown {
  return Reflect.get(fields, key);
}
export function writeField(fields: object, key: string, value: unknown) {
  // Runtime metadata is string-keyed; membership keeps this bridge from creating unknown fields.
  if (key in fields) Reflect.set(fields, key, value);
}
